import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../shared/apiClient';
import type { Odl } from '../shared/types';
import {
  ClipboardList,
  HardHat,
  ChevronRight,
  Paperclip,
  Calendar,
} from 'lucide-react';

interface OdlApiResponse {
  ok: boolean;
  data: Odl[];
  contatori: Record<string, number>;
}

export function OdlListPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'attivi' | 'da_terminare'>('attivi');

  const { data: odlRes, isLoading } = useQuery<OdlApiResponse>({
    queryKey: ['odl-squadra-list'],
    queryFn: async () => {
      const res = await apiGet<OdlApiResponse>('/odl');
      return res.data;
    },
    refetchInterval: 15_000,
  });

  const lista: Odl[] = odlRes?.data || [];

  const odlAttivi = lista.filter((o: Odl) =>
    o.stato === 'assegnato' || o.stato === 'preso_in_carico'
  );
  const odlDaTerminare = lista.filter((o: Odl) => o.stato === 'da_terminare');

  const daMostrare = tab === 'attivi' ? odlAttivi : odlDaTerminare;

  return (
    <div className="space-y-4 pb-12">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-sky-600" />
            Ordini Di Lavoro (ODL)
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Lavorazioni straordinarie e cantieri programmati
          </p>
        </div>
      </div>

      {/* Tabs stile Linear Segmented Control */}
      <div className="flex rounded-lg bg-slate-200/60 p-1 border border-slate-200/60">
        <button
          type="button"
          onClick={() => setTab('attivi')}
          className={`flex-1 py-1.5 px-2 text-xs font-bold rounded-md transition flex items-center justify-center gap-1.5 ${
            tab === 'attivi'
              ? 'bg-white text-indigo-700 shadow-xs border border-slate-200/60'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <span>Attivi in lavorazione</span>
          <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${tab === 'attivi' ? 'bg-indigo-50 text-indigo-700' : 'bg-slate-200 text-slate-700'}`}>
            {odlAttivi.length}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setTab('da_terminare')}
          className={`flex-1 py-1.5 px-2 text-xs font-bold rounded-md transition flex items-center justify-center gap-1.5 ${
            tab === 'da_terminare'
              ? 'bg-white text-amber-800 shadow-xs border border-slate-200/60'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <span>Da terminare</span>
          <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${tab === 'da_terminare' ? 'bg-amber-100 text-amber-800' : 'bg-slate-200 text-slate-700'}`}>
            {odlDaTerminare.length}
          </span>
        </button>
      </div>

      {/* Elenco ODL */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-3 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-xs text-slate-500 mt-2">Caricamento ordini di lavoro...</p>
        </div>
      ) : daMostrare.length === 0 ? (
        <div className="card-section text-center py-10">
          <ClipboardList className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="text-sm font-semibold text-slate-700">
            {tab === 'attivi' ? 'Nessun ODL assegnato al momento' : 'Nessun ODL in attesa di essere terminato'}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {tab === 'attivi' ? 'Gli ordini assegnati dalla centrale compariranno qui' : 'I cantieri sospesi nei giorni precedenti compariranno qui'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {daMostrare.map((odl: Odl) => {
            const isPreso = odl.stato === 'preso_in_carico';
            const isDaTerminare = odl.stato === 'da_terminare';

            return (
              <div
                key={odl.id}
                onClick={() => navigate(`/odl/${odl.id}`)}
                className={`ln-card p-3.5 sm:p-4 cursor-pointer transition-all hover:border-sky-300 border-l-4 ${
                  isPreso
                    ? 'border-l-indigo-500 bg-indigo-50/20'
                    : isDaTerminare
                    ? 'border-l-amber-500 bg-amber-50/20'
                    : 'border-l-sky-500'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="space-y-1.5 flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs font-bold px-2 py-0.5 rounded bg-sky-100 text-sky-800">
                        ODL {odl.numeroOrdineCommittente}
                      </span>
                      {isPreso && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 animate-pulse">
                          IN CORSO
                        </span>
                      )}
                      {isDaTerminare && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                          DA TERMINARE
                        </span>
                      )}
                      {odl.priorita === 'urgente' && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700">
                          URGENTE
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="ln-impianto-chip text-[11px] py-0.5 px-2 font-bold">
                        [{odl.impianto.codice}]
                      </span>
                      <span className="text-sm font-bold text-slate-900 break-words flex-1">
                        {odl.impianto.nome}
                      </span>
                    </div>

                    <p className="text-xs text-slate-600 line-clamp-2">
                      {odl.descrizioneLavoro}
                    </p>

                    <div className="flex items-center gap-3 text-[11px] text-slate-500 pt-1">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {new Date(odl.dataApertura).toLocaleDateString('it-IT')}
                      </span>
                      {odl._count?.reports !== undefined && (
                        <span className="flex items-center gap-1">
                          <HardHat className="w-3.5 h-3.5" />
                          {odl._count.reports} giornate
                        </span>
                      )}
                      {odl._count?.allegati !== undefined && odl._count.allegati > 0 && (
                        <span className="flex items-center gap-1 text-sky-600 font-semibold">
                          <Paperclip className="w-3.5 h-3.5" />
                          {odl._count.allegati} allegati
                        </span>
                      )}
                    </div>

                    {isPreso && (
                      <div className="pt-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/odl/${odl.id}/report`);
                          }}
                          className="w-full py-2 px-3 bg-sky-600 hover:bg-sky-700 text-white font-bold text-xs rounded-lg flex items-center justify-center gap-1.5 shadow-sm"
                        >
                          <ClipboardList className="w-3.5 h-3.5" />
                          <span>Compila Report di Giornata</span>
                        </button>
                      </div>
                    )}
                  </div>

                  <ChevronRight className="w-5 h-5 text-slate-400 shrink-0 self-center" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
